import React, { forwardRef, useState } from 'react';
import { Input as InputUI } from '@/components/ui/input';
import LocalIcon from '../Icons/LocalIcon';
import ClearSvg from '@/src/assets/icons/icon-input-clear.svg';
import LucideIcon from '../Icons/LucideIcon';
import { cn } from '@/src/utils';
import { twMerge } from 'tailwind-merge';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  containerClassName?: string;
  inputClassName?: string;
  allowClear?: boolean;
  onClear?: () => void;
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  onEnterKeyDown?: () => void;
  size?: 40 | 32;
  isEnterSubmit?: boolean;
  isDecimal?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      containerClassName,
      allowClear = true,
      type,
      onClear,
      inputClassName,
      onFocus,
      onBlur,
      size = 40,
      onEnterKeyDown,
      onKeyDown,
      className,
      onChange,
      isEnterSubmit = false,
      isDecimal = false,
      ...props
    },
    ref,
  ) => {
    const [value, setValue] = useState(props.value);
    const [inputType, setInputType] = useState(type);
    const [isPasswordType, setIsPasswordType] = useState(false);
    const [isFocus, setIsFocus] = useState(false);

    const handlePasswordVisivleIcon = () => {
      if (isPasswordType) {
        setInputType(type);
      } else {
        setInputType('text');
      }

      setIsPasswordType(prev => {
        return !prev;
      });
    };

    const renderPasswordIcon = () => {
      if (type === 'password') {
        if (isPasswordType) {
          return (
            <LucideIcon
              name="Eye"
              size={16}
              onClick={handlePasswordVisivleIcon}
              color={props.disabled ? 'gray6' : 'gray9'}
            />
          );
        }

        return (
          <LucideIcon
            name="EyeOff"
            size={16}
            onClick={handlePasswordVisivleIcon}
            color={props.disabled ? 'gray6' : 'gray9'}
          />
        );
      }
    };

    const focusInputStyle = isFocus ? 'border-gray9' : '';
    const disabledInputStyle = props.disabled
      ? 'bg-gray1 border-gray3 text-gray6'
      : 'bg-white';
    const disabledIconContainerStyle = props.disabled
      ? 'bg-transparent'
      : 'bg-white';

    const containerSizeStyle = size === 40 ? 'h-[40px]' : 'h-[32px]';

    return (
      <div
        className={cn(
          `${focusInputStyle} group flex items-center rounded-md border px-3 py-2`,
          containerClassName,
          disabledInputStyle,
          containerSizeStyle,
        )}
      >
        <InputUI
          className={twMerge(
            'h-auto rounded-none border border-none bg-transparent p-0 text-b16r placeholder:text-b16r placeholder:text-gray4 focus-visible:border-gray9 focus-visible:ring-0 focus-visible:ring-offset-0 disabled:placeholder:text-gray6',
            inputClassName,
            className,
          )}
          ref={ref}
          type={inputType}
          value={value}
          onFocus={e => {
            onFocus?.(e);
            setIsFocus(true);
          }}
          onBlur={e => {
            onBlur?.(e);
            setIsFocus(false);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              onEnterKeyDown?.();
              if (!isEnterSubmit) {
                e.preventDefault();
              }
            }

            if (Number(props.min) === 0) {
              if (
                e.key === 'e' ||
                e.key === 'E' ||
                e.key === '-' ||
                (!isDecimal && e.key === '.')
              ) {
                e.preventDefault();
              }
            }

            onKeyDown?.(e);
          }}
          onChange={e => {
            if (Number(props.min) === 0) {
              const newValue = isDecimal
                ? e.target.value
                : e.target.value.replace(/[.eE-]/g, '');

              onChange?.({ target: { value: newValue } } as any);
            } else {
              onChange?.(e);
            }
          }}
          {...props}
        />

        <figure
          className={cn(
            'flex items-center gap-2  pl-2',
            disabledIconContainerStyle,
          )}
        >
          {allowClear && !props.disabled && !!props.value && (
            <LocalIcon
              iconSrc={ClearSvg}
              iconSize={16}
              onPointerDown={() => {
                onClear?.();
                setValue('');
              }}
            />
          )}
          {renderPasswordIcon()}
        </figure>
      </div>
    );
  },
);

export default Input;
